import { Controller } from 'stimulus';
import { t } from '../javascript/lib/i18n';

/**
 * Allow to ignore inputs in specific conditions:
 * - Non POST forms (unless data-confirm-navigation-force attribute is present)
 * - Marked with `data-confirm-navigation-ignore-input` or within an element marked as such
 */
const SELECTOR_IGNORE_INPUT = `
  form:not([method="post"]):not([data-confirm-navigation-force]) *,
  [data-confirm-navigation-ignore-input-within] * ,
  [data-confirm-navigation-ignore-input]
`;

/**
 * Monitors `input` on form controls within POST forms
 * and prompts users before they navigate away or take specific actions
 * (outside of submitting the form, of course).
 *
 * Supports:
 *  - browser navigation: page reloads, entering a URL, clicking a bookmark,
 *    back/forward navigation *leading outside the app*
 *  - Turbo drive: prompts before visiting and cancels the visit if user doesn't confirm.
 *    It *does not* handle back/forward navigation through Turbo Drive due to Turbo
 *    not having events to intercept visits happening through history manipulation.
 *    https://github.com/hotwired/turbo/issues/485
 *  - specific actions: use `data-action="EVENT->confirm-navigation#confirmIfNeeded"`
 *    to hook a confirmation on specific actions.
 *    The controller will prevent the event's default if the user doesn't confirm.
 *
 * The controller ignores any field that's not in a `method="post"` form.
 * It'll also ignore any field marked with `data-confirm-navigation-ignore-input`
 * or within an element marked with `data-confirm-navigation-ignore-input-within`.
 *
 * > Note: If form submission was to happen within a Turbo Frame,
 * > we'll need to introduce a way to clean up whether
 * > we still require confirmation after successful submissions
 * > This'll likely require to track the `name` or `id` of the modified fields
 * > and match them against the data of the `turbo:submit-end` event
 * > https://turbo.hotwired.dev/reference/events
 *
 * --- When the relevant events fire ---
 *
 * 1. submit (native)
 *    Fires when the form is submitted (submit button click or form.submit()).
 *    Bubbles from the form. Turbo intercepts the submission, so this can run
 *    before or alongside Turbo's own events.
 *
 * 2. turbo:submit-start
 *    Fired by Turbo when it begins handling a form submission, before the
 *    fetch is sent. Order: user submits → Turbo intercepts → turbo:submit-start
 *    → fetch starts. Event target is the form. We set isSubmit here so we know
 *    a POST is in progress.
 *
 * 3. turbo:submit-end
 *    Fired by Turbo when the fetch completes (success or failure). Order: server
 *    responds → turbo:submit-end (event.detail.success, event.detail.fetchResponse).
 *    For a 302 response, Turbo then follows the redirect (see before-visit).
 *    We clear isSubmit here except for redirects.
 *
 * 4. turbo:before-visit
 *    Fired by Turbo Drive when a visit is about to happen: link click, form
 *    redirect, history/back/forward, or programmatic visit. Order: something
 *    triggers a visit → turbo:before-visit → if not prevented, the visit runs.
 *    For a form that returns a redirect, the "happy path" is: submit-start →
 *    fetch → submit-end → Turbo follows redirect → before-visit (for that
 *    redirect). So we expect before-visit after submit-end for redirects, and we
 *    keep isSubmit true for redirects so we don't prompt. When the server or
 *    Turbo is slow, or with frame submissions, before-visit can sometimes fire
 *    before submit-end (or in an order where we've already cleared state), which
 *    can cause a spurious "unsaved changes" prompt; a grace window after
 *    submit-start can avoid that.
 *
 * 5. turbo:submit-success (our custom event)
 *    Dispatched on the form from turbo:submit-end when detail.success is true.
 *    Not used by this controller; we rely on submit-end and unregisterAlerts on
 *    success there.
 *
 * Happy path (POST form → 302 redirect) — isSubmit lifecycle:
 *
 *   User clicks Save
 *        │
 *        ▼  submit (native)  ───────────  [fallback: isSubmit = true in onSubmit]
 *        │
 *        ▼  turbo:submit-start  ─────────  SET isSubmit = true
 *        │
 *        ▼  fetch (in flight)
 *        │
 *        ▼  Server responds 302
 *        │
 *        ▼  turbo:submit-end  ───────────  Keep isSubmit true (redirect); unregisterAlerts on success
 *        │
 *        ▼  Turbo follows redirect
 *        │
 *        ▼  turbo:before-visit  ─────────  CHECK isSubmit → true → do not prompt
 *        │
 *        ▼  Visit proceeds, page updates
 *
 */
export default class extends Controller {
  initialize() {
    this.onInput = this.onInput.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitStart = this.onSubmitStart.bind(this);
    this.onSubmitEnd = this.onSubmitEnd.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
    this.onBeforeVisit = this.onBeforeVisit.bind(this);
  }

  connect() {
    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('cocoon:after-remove', this.onInput);
    this.element.addEventListener('cocoon:after-insert', this.onInput);
    // Unfortunately, the `turbo:before-visit` event doesn't tell
    // if it's a link click or a form submission, so we need
    // to track this ourselves
    // Listen to turbo:submit-start which fires BEFORE turbo:before-visit
    // to avoid race condition where before-visit fires before submit event
    this.element.addEventListener('turbo:submit-start', this.onSubmitStart);
    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd);
    this.element.addEventListener('amba:fetch-error', this.onSubmitEnd);
  }

  disconnect() {
    this.unregisterAlerts();
    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('cocoon:after-remove', this.onInput);
    this.element.removeEventListener('cocoon:after-insert', this.onInput);
    this.element.removeEventListener('turbo:submit-start', this.onSubmitStart);
    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd);
    this.element.removeEventListener('amba:fetch-error', this.onSubmitEnd);
  }

  confirmIfNeeded(event) {
    if (this.registeredAlerts) {
      if (confirm(t('js.confirm_navigation.prompt'))) {
        this.unregisterAlerts();
      } else {
        // Ideally we'd want to only `preventDefault`
        // in case other controllers would be interested
        // in the event.
        // If possible, please update whatever handles
        // the event with `if (!event.defaultPrevented)`
        // before adding `event.stop[Immediate]Propagation()`
        event.preventDefault();
      }
    }
  }

  onInput(event) {
    if (!event.target.matches(SELECTOR_IGNORE_INPUT)) {
      this.registerAlerts();
    }
  }

  onSubmitStart(event) {
    // turbo:submit-start fires BEFORE turbo:before-visit for form submissions
    // Check if the form is within our element and is a POST form
    const form = event.target;
    if (form && form.tagName === 'FORM' && this.element.contains(form)) {
      // GET submissions are navigations, so we only want to mark
      // that we're submitting for POST forms
      this.isSubmit = form.method.toLowerCase() === 'post';
    }
  }

  onSubmit(event) {
    // GET submissions are navigations, so we only want to mark
    // that we're submitting for POST forms
    // This is a fallback in case turbo:submit-start didn't fire
    this.isSubmit = event.target.method == 'post';
  }

  onSubmitEnd(event) {
    const form = event?.target;
    const success = event?.detail?.success;
    const fetchResponse = event?.detail?.fetchResponse;
    const isRedirect = fetchResponse?.redirected;

    // For redirects, keep isSubmit true so the redirect navigation
    // doesn't trigger a confirm dialog. turbo:before-visit fires AFTER
    // turbo:submit-end for redirects, and needs to see isSubmit = true.
    // For non-redirects, defer clearing so any before-visit that fires in the
    // same turn (e.g. frame update) still sees isSubmit true and we don't prompt.
    if (!isRedirect) {
      setTimeout(() => {
        this.isSubmit = false;
      }, 0);
    }

    // For successful form submissions, clear the dirty state.
    // The data has been saved, so there are no "unsaved changes" anymore.
    // This handles both full page redirects and turbo-frame submissions.
    if (form?.tagName === 'FORM' && success) {
      this.unregisterAlerts();
    }
  }

  registerAlerts() {
    if (!this.registeredAlerts) {
      window.onbeforeunload = this.onBeforeUnload;
      document.addEventListener('turbo:before-visit', this.onBeforeVisit);
      this.registeredAlerts = true;
    }
  }

  unregisterAlerts() {
    this.isDirty = false;
    window.onbeforeunload = null;
    document.removeEventListener('turbo:before-visit', this.onBeforeVisit);
    this.registeredAlerts = false;
  }

  onBeforeUnload() {
    // Only older browsers will show the custom message as Firefox, Chrome and Safari all
    // have dropped displaying a custom message. However, the method needs to return something
    // so may as well be our custom message.
    // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload#browser_compatibility
    // https://bugzilla.mozilla.org/show_bug.cgi?id=588292
    // https://chromestatus.com/feature/5349061406228480
    // https://developer.apple.com/library/archive/releasenotes/General/WhatsNewInSafari/Articles/Safari_9_1.html#//apple_ref/doc/uid/TP40014305-CH10-SW11

    // Don't hold up POST form submissions
    if (!this.isSubmit) {
      return t('js.confirm_navigation.prompt');
    }
  }

  onBeforeVisit(event) {
    // Check if this navigation is from a form submission
    // turbo:submit-start should have set isSubmit, but also check
    // if the active element is a submit button as a fallback
    const activeElement = document.activeElement;
    const isFormSubmit =
      this.isSubmit ||
      (activeElement &&
        ((activeElement.tagName === 'BUTTON' &&
          activeElement.type === 'submit') ||
          (activeElement.tagName === 'INPUT' &&
            activeElement.type === 'submit')));

    if (!isFormSubmit) {
      if (confirm(t('js.confirm_navigation.prompt'))) {
        this.unregisterAlerts();
      } else {
        event.preventDefault();
      }
    }
  }
}
